import { css } from '@linaria/atomic';
import { useTranslation } from '@frontend/i18n';
import { Button, InlineNotification, Modal, ModalControlModalProps, Text } from '@frontend/design-system';
import type { PaymentFieldType } from './payment-field.helpers';

// Same size as `PaymentMethodSetupModal`.
const MODAL_WIDTH = 480;

interface PaymentPreviewModalProps {
  modalProps: ModalControlModalProps;
  title: React.ReactNode;
  type: PaymentFieldType;
}

/** Static mock of the setup modal for form preview; nothing is sent to the processor. */
export const PaymentPreviewModal = ({ modalProps, title, type }: PaymentPreviewModalProps) => {
  const { t } = useTranslation('digital-forms-experience');
  const labels =
    type === 'cardOnFile'
      ? [t('Cardholder Name'), t('Card Number'), t('Expiry'), t('Security Code'), t('Country')]
      : [t('Email'), t('Full name'), t('Search for your bank')];

  return (
    <Modal {...modalProps} className={modalStyle} minWidth={MODAL_WIDTH} maxWidth={MODAL_WIDTH}>
      <Modal.Header onClose={modalProps.onClose}>{title}</Modal.Header>
      <Modal.Body>
        <div className={bodyStyle}>
          <InlineNotification status='info'>{t('This is just a preview of the fields')}</InlineNotification>
          {labels.map((label) => (
            <div key={label} className={mockInputStyle} aria-hidden>
              <Text size='small' color='subdued'>
                {label}
              </Text>
            </div>
          ))}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <div className={actionsStyle}>
          <Button variant='secondary' onClick={modalProps.onClose}>
            {t('Cancel')}
          </Button>
          <Button onClick={modalProps.onClose}>{t('Save')}</Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

const modalStyle = css`
  && {
    padding-top: var(--fabric-space-padding-md);
    padding-bottom: var(--fabric-space-padding-md);
  }

  & .wv-modal-header,
  & .wv-modal-flex-content,
  & .wv-modal-footer {
    padding-left: var(--fabric-space-padding-md);
    padding-right: var(--fabric-space-padding-md);
  }
`;

const bodyStyle = css`
  display: flex;
  flex-direction: column;
  row-gap: var(--fabric-space-gap-md);
  column-gap: var(--fabric-space-gap-md);
`;

const mockInputStyle = css`
  border-top: 1px solid var(--fabric-color-neutral-30);
  border-right: 1px solid var(--fabric-color-neutral-30);
  border-bottom: 1px solid var(--fabric-color-neutral-30);
  border-left: 1px solid var(--fabric-color-neutral-30);
  border-top-left-radius: var(--fabric-radius-md);
  border-top-right-radius: var(--fabric-radius-md);
  border-bottom-left-radius: var(--fabric-radius-md);
  border-bottom-right-radius: var(--fabric-radius-md);
  padding-top: var(--fabric-space-padding-sm);
  padding-right: var(--fabric-space-padding-sm);
  padding-bottom: var(--fabric-space-padding-sm);
  padding-left: var(--fabric-space-padding-sm);

  p {
    margin-top: 0;
    margin-right: 0;
    margin-bottom: 0;
    margin-left: 0;
  }
`;

const actionsStyle = css`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  column-gap: var(--fabric-space-gap-md);
`;
